import * as React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Collapse,
  CssBaseline,
  Tooltip,
} from "@material-ui/core";
import {
  makeStyles,
  createTheme,
  ThemeProvider,
} from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import EmployeeForm, { emptyEmployee } from "./employeeForm";
import CommonButton from "../button";
import tokens from "../../theme";

const { colors, fontFamily } = tokens;

const theme = createTheme({
  palette: {
    type: "light",
    primary: { main: colors.DodgerBlue, contrastText: colors.White },
    secondary: { main: colors.MintGreen, contrastText: colors.Violet },
    error: { main: colors.RadicalRed },
    background: { default: colors.Porcelain, paper: colors.White },
    text: { primary: colors.Violet, secondary: "#5A5A7A" },
    divider: colors.Gallery,
  },
  typography: { fontFamily: fontFamily.MARKPRO },
  shape: { borderRadius: 10 },
});

const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: colors.Porcelain,
    height: "500px",
    overflowY: "auto",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  container: {
    maxWidth: 960,
    margin: "0 auto",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    color: colors.DodgerBlue, fontSize: "24px", fontFamily: fontFamily.MARKPROBOLD, marginBottom: "20px" 

  },
  counter: {
    color: "#5A5A7A",
    fontFamily: fontFamily.MARKPROBOOK,
  },
  list: {
    display: "flex",
    flexDirection: "column",
  },
  entry: {
    backgroundColor: colors.White,
    border: `1px solid ${colors.Gallery}`,
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  entryHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    cursor: "pointer",
    "&:hover": { backgroundColor: colors.Zumthor },
  },
  entryHeaderActive: {
    backgroundColor: colors.Zumthor,
    borderBottom: `1px solid ${colors.Gallery}`,
  },
  badge: {
    minWidth: 32,
    height: 32,
    borderRadius: "50%",
    backgroundColor: colors.DodgerBlue,
    color: colors.White,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: fontFamily.MARKPROBOLD,
    fontSize: 14,
    marginRight: theme.spacing(1.5),
  },
  summary: {
    flexGrow: 1,
    minWidth: 0,
  },
  summaryName: {
    color: colors.Violet,
    fontFamily: fontFamily.MARKPROMEDIUM,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  summaryMeta: {
    color: "#5A5A7A",
    fontFamily: fontFamily.MARKPROBOOK,
    fontSize: 13,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  removeIcon: { color: colors.RadicalRed },
  toggleIcon: { color: colors.DodgerBlue },
  formBody: {
    padding: theme.spacing(2, 3, 3),
  },
  addBtn: {
    width: "100%",
    border: `1.5px dashed ${colors.Periwinkle}`,
    color: colors.DodgerBlue,
    fontFamily: fontFamily.MARKPROMEDIUM,
    textTransform: "none",
    padding: theme.spacing(2),
    "&:hover": {
      backgroundColor: colors.Zumthor,
      borderColor: colors.DodgerBlue,
    },
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "flex-end",
  },
  submitBtn: {
    backgroundColor: colors.DodgerBlue,
    color: colors.White,
    fontFamily: fontFamily.MARKPROMEDIUM,
    textTransform: "none",
    paddingLeft: 28,
    paddingRight: 28,
    paddingTop: 10,
    paddingBottom: 10,
    borderRadius: 10,
    marginLeft: theme.spacing(2),
    "&:hover": { backgroundColor: colors.DodgerBlueLight },
    "&.Mui-disabled": {
      backgroundColor: colors.Periwinkle,
      color: colors.White,
    },
  },
  resetBtn: {
    borderColor: colors.Periwinkle,
    color: colors.DodgerBlue,
    fontFamily: fontFamily.MARKPROMEDIUM,
    textTransform: "none",
    paddingLeft: 28,
    paddingRight: 28,
    paddingTop: 10,
    paddingBottom: 10,
    borderRadius: 10,
    "&:hover": {
      backgroundColor: colors.Zumthor,
      borderColor: colors.DodgerBlue,
    },
  },
  successBanner: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: colors.MintGreen,
    color: colors.Violet,
    fontFamily: fontFamily.MARKPROMEDIUM,
    textAlign: "center",
  },
}));

const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const summarize = (emp) => {
  const name = [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim();
  const id = emp.employee_id;
  const email = emp.email;
  return {
    name: name || "Untitled employee",
    meta: [id, email].filter(Boolean).join("  ·  "),
  };
};

const isComplete = (emp) =>
  emp.first_name &&
  emp.last_name &&
  emp.employee_id &&
  emp.email &&
  emp.profile.phone;

function EmployeeFormListInner({ onSubmit, canCommit = true }) {
  const classes = useStyles();
  const [entries, setEntries] = useState([
    { id: genId(), data: emptyEmployee(), open: true },
  ]);
  const [submitted, setSubmitted] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const updateEntry = (id, patch) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const handleChange = (id) => (data) => updateEntry(id, { data });

  const toggleOpen = (id) =>
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, open: !e.open } : e))
    );

  const handleAdd = () =>
    setEntries((prev) => [
      ...prev.map((e) => ({ ...e, open: false })),
      { id: genId(), data: emptyEmployee(), open: true },
    ]);

  const handleRemove = (id) => (event) => {
    event.stopPropagation();
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleSubmit = () => {
    const payload = entries.map((e) => e.data);
    setSubmitted(true);
    if (onSubmit) onSubmit(payload);
    handleReset();
  };

  const handleReset = () => {
    setEntries([{ id: genId(), data: emptyEmployee(), open: false }]);
    setSubmitted(false);
  };

  const canSubmit =
    entries.length > 0 && entries.every((e) => isComplete(e.data));

  return (
    <Box className={classes.page}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Typography variant="h5" className={classes.title}>
            Add Employees
          </Typography>
          <Typography variant="body2" className={classes.counter}>
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </Typography>
        </Box>

        <Box className={classes.list}>
          {entries.map((entry, idx) => {
            const summary = summarize(entry.data);
            return (
              <Box key={entry.id} className={classes.entry}>
                <Box
                  className={`${classes.entryHeader} ${entry.open ? classes.entryHeaderActive : ""
                    }`}
                  onClick={() => toggleOpen(entry.id)}
                >
                  <Box className={classes.badge}>{idx + 1}</Box>
                  <Box className={classes.summary}>
                    <Typography className={classes.summaryName}>
                      {summary.name}
                    </Typography>
                    {summary.meta && (
                      <Typography className={classes.summaryMeta}>
                        {summary.meta}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleRemove(entry.id)}
                    disabled={entries.length === 1}
                    aria-label="Remove entry"
                  >
                    <DeleteOutlineIcon
                      fontSize="small"
                      className={classes.removeIcon}
                    />
                  </IconButton>
                  <IconButton size="small" aria-label="Toggle entry">
                    {entry.open ? (
                      <ExpandLessIcon className={classes.toggleIcon} />
                    ) : (
                      <ExpandMoreIcon className={classes.toggleIcon} />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={entry.open} unmountOnExit>
                  <Box className={classes.formBody}>
                    <EmployeeForm
                      value={entry.data}
                      onChange={handleChange(entry.id)}
                      setIsValid={(valid) => {
                        setIsValid(valid);
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>

        <Button
          className={classes.addBtn}
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add another employee
        </Button>

        {submitted && (
          <Box className={classes.successBanner}>
            {entries.length} {entries.length === 1 ? "employee" : "employees"} submitted
          </Box>
        )}

        <Box className={classes.actions}>
          <CommonButton
            variant="outlined"
            className={classes.resetBtn}
            onClick={handleReset}
            text="Reset"
          />
            
          {/* </CommonButton> */}
          <Tooltip title={!canCommit ? "You need employee upload (create) rights to onboard." : ""}>
            <span>
              <CommonButton
                variant="contained"
                className={classes.submitBtn}
                disabled={!canSubmit || !isValid || !canCommit}
                onClick={handleSubmit}
                text={`Submit ${entries.length}
                ${entries.length === 1 ? "employee" : "employees"}`}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

export default function EmployeeFormList(props) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <EmployeeFormListInner {...props} />
    </ThemeProvider>
  );
}
